/* Graph of Life brand v1.1.0 · tokens.json */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import InteractiveBackground from '@/components/ui/InteractiveBackground';
import ProofCard from '@/components/gol/ProofCard';
import SectionHeading from '@/components/gol/SectionHeading';
import { ReviewCta, GhostLink } from '@/components/gol/ReviewCta';
import SystemsCompose from '@/components/visualizations/SystemsCompose';
import { ReadingNext } from '@/components/gol/ReadingNext';
import { proofSystems, proofCount, evidenceStates } from '@/data/proofRegistry';

const FILTERS = [
  { id: 'all', label: 'All systems' },
  { id: 'operational', label: evidenceStates.operational.label },
  { id: 'indicative', label: evidenceStates.indicative.label },
  { id: 'exploratory', label: evidenceStates.exploratory.label },
  { id: 'screening', label: evidenceStates.screening.label },
];

const Systems = () => {
  const [filter, setFilter] = useState('all');
  const shown = filter === 'all' ? proofSystems : proofSystems.filter((p) => p.evidenceState === filter);

  return (
    <div data-testid="systems-page" className="bg-abyss-950 text-sand-50 relative overflow-x-hidden">
      <InteractiveBackground viewMode="FINANCIAL" density={0.8} />

      <div className="gol-vline fixed left-5 top-0 bottom-0 z-30 hidden lg:block" aria-hidden="true" />
      <div className="gol-vline fixed right-5 top-0 bottom-0 z-30 hidden lg:block" aria-hidden="true" />

      <section className="relative px-5 md:px-8 pt-32 md:pt-40 pb-16">
        <div className="gol-grid absolute inset-0 opacity-50 pointer-events-none" aria-hidden="true" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-6xl mx-auto relative z-10 space-y-8"
        >
          <div className="inline-flex items-center gap-3 px-3 py-1.5 rounded-sm border border-teal-500/25 bg-teal-500/5 backdrop-blur-sm">
            <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-400" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-teal-400">
              Proof library · {proofCount} working systems
            </span>
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light leading-[1.05] tracking-tight max-w-3xl">
            Every proof states <span className="text-teal-400 gol-glow">its limit.</span>
          </h1>

          <p className="text-base md:text-lg font-light leading-relaxed text-sand-50/65 max-w-2xl">
            Six investigations, from molecular pathways to whole watersheds. Each connects a question to its sources,
            method and uncertainty. Together, they show what exploratory screening and diligence preparation look like in practice.
          </p>

          <div className="flex flex-wrap items-center gap-6">
            <ReviewCta label="Scope a Review" location="proof-library-header" testId="systems-scope-review" />
            <GhostLink to="/methodology" testId="systems-method-link">
              How the work proceeds
            </GhostLink>
          </div>

          <div className="flex flex-wrap gap-2 pt-4" role="group" aria-label="Filter by evidence state" data-testid="proof-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`proof-filter-${f.id}`}
                aria-pressed={filter === f.id}
                className={`px-3.5 py-1.5 rounded-sm border font-mono text-[10px] uppercase tracking-[0.16em] transition-all duration-300 ${
                  filter === f.id
                    ? 'border-teal-400 text-teal-300 bg-teal-500/15 shadow-[0_0_18px_rgba(45,212,191,0.25)]'
                    : 'border-abyss-800 text-sand-50/45 hover:text-teal-300 hover:border-teal-500/40'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <p className="font-mono text-[11px] text-sand-50/60" role="status" data-testid="proof-results-count">{shown.length} of {proofCount} systems · {FILTERS.find(f => f.id === filter).label}</p>
        </motion.div>
      </section>

      <section data-testid="proof-library" className="relative px-5 md:px-8 py-16 md:py-24 border-t border-abyss-900">
        <div className="max-w-6xl mx-auto grid gap-6 lg:grid-cols-2">
          {shown.map((proof, i) => (
            <ProofCard key={proof.id} proof={proof} emphasis={i === 0 ? 'lead' : 'standard'} />
          ))}
        </div>
      </section>

      <SystemsCompose />

      <section className="relative px-5 md:px-8 py-24 border-t border-abyss-900">
        <div className="max-w-6xl mx-auto space-y-10">
          <SectionHeading eyebrow="Where this fits" heading="One bounded decision at a time.">
            <p>
              A Dependency Exposure Review brings this discipline to your question. Scope stays focused,
              evidence stays traceable, next steps stay explicit.
            </p>
          </SectionHeading>
          <div className="flex flex-wrap items-center gap-6">
            <Link
              to="/"
              data-testid="systems-back-link"
              className="font-mono text-xs uppercase tracking-[0.16em] text-sand-50/50 hover:text-teal-300 transition-colors duration-300"
            >
              Back to overview
            </Link>
          </div>
          <ReadingNext id="systems" ctaTestId="systems-footer-review" to="/methodology" title="Follow the method behind the proofs." copy="How the decision, ecological relationships and source evidence shape a bounded review." />
        </div>
      </section>
    </div>
  );
};

export default Systems;
